import { useEffect, useId, useState } from 'react';

import { useI18n } from '../i18n/I18nProvider';
import { Button } from '../ui/Button';
import { Sheet } from '../ui/Sheet';
import { Icon } from '../ui/Icon';
import { clearRef, pendingRef } from '../referral/api';
import { deviceName, errorKey, type Session } from './api';
import type { AuthApi } from './api';
import { availableCountries, parsePhoneInput, phoneIdentifier, type PhoneConfig } from './phone';
import './auth.css';

type Mode = 'login' | 'register';

export function AuthSheet(
  { open, api, onClose, onDone }:
  { open: boolean; api: AuthApi; onClose(): void; onDone(s: Session): void },
) {
  const { t, locale } = useI18n();
  const [mode, setMode] = useState<Mode>('login');
  const [country, setCountry] = useState('');
  const [phoneConfig, setPhoneConfig] = useState<PhoneConfig | null>(null);
  const [configErr, setConfigErr] = useState(false);
  const [configRetry, setConfigRetry] = useState(0);
  const [phone, setPhone] = useState('');
  const phoneId = useId();
  const phoneHintId = useId();
  const countries = availableCountries(phoneConfig, mode);
  const selectedCountry = countries.find((c) => c.code === country)
    ?? countries.find((c) => c.code === phoneConfig?.defaultCountry) ?? countries[0];
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setPhoneConfig(null); setConfigErr(false);
    api.phoneConfig().then((config) => { if (alive) setPhoneConfig(config); })
      .catch(() => { if (alive) setConfigErr(true); });
    return () => { alive = false; };
  }, [api, open, configRetry]);

  useEffect(() => {
    if (!phoneConfig) return;
    const allowed = availableCountries(phoneConfig, mode);
    if (!allowed.some((c) => c.code === country)) {
      setCountry(allowed.find((c) => c.code === phoneConfig.defaultCountry)?.code ?? allowed[0]?.code ?? '');
      // A policy change must not reinterpret a previously entered number under another country.
      setPhone('');
    }
  }, [phoneConfig, mode, country]);

  function changeMode() {
    setMode(mode === 'login' ? 'register' : 'login');
    setCountry(''); setPhone(''); setErr('');
    setConfigRetry((n) => n + 1);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!phoneConfig || !selectedCountry || busy) return;
    const identifier = phoneIdentifier(selectedCountry.code, phone, countries);
    if (!identifier) { setErr(t('err.BAD_PHONE')); return; }
    setBusy(true);
    setErr('');
    try {
      const dev = deviceName(navigator.userAgent);
      const s = mode === 'login'
        ? await api.login(identifier, pw, dev)
        // 带上从分享链接里捡到的邀请码。**只在注册时带** ——
        // 登录是老用户，他的上级早就定死了。
        : await api.register(identifier, pw, dev, pendingRef());
      // 注册成功才清掉：失败了（密码太弱、手机号占用）他还会再试一次，
      // 那一次仍然该算这个上级的。
      if (mode === 'register') clearRef();
      setPw('');
      onDone(s);
    } catch (e2) {
      // 后端只回稳定的 code，**文案在前端翻** —— 后端不知道用户选了哪种语言
      setErr(t(errorKey(e2)));
      if (errorKey(e2) === 'err.REGISTRATION_COUNTRY_DISABLED') setConfigRetry((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open}
           title={t(mode === 'login' ? 'action.login' : 'action.register')}
           onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        {!phoneConfig && <div className="stack">
          <p className="small dim" role={configErr ? 'alert' : 'status'}>
            {t(configErr ? 'auth.countryLoadFailed' : 'auth.countryLoading')}
          </p>
          {configErr && <Button variant="ghost" type="button" onClick={() => setConfigRetry((n) => n + 1)}>{t('action.retry')}</Button>}
        </div>}
        {phoneConfig && !selectedCountry && <p className="small dim" role="status">{t('auth.registrationClosed')}</p>}
        {selectedCountry && <div className="field">
          <label className="field__label" htmlFor={phoneId}>{t('auth.phone')}</label>
          <div className="auth-phone">
            <div className="auth-phone__country">
              <span className="auth-phone__selected" aria-hidden="true">
                <span className="auth-phone__flag">{selectedCountry.flag}</span>
                <span className="num">{selectedCountry.dialCode}</span>
                <Icon name="chevron" className="auth-phone__chevron" />
              </span>
              <select className="auth-phone__select" aria-label={t('auth.countryCode')}
                      value={selectedCountry.code} disabled={busy}
                      onChange={(e) => { setCountry(e.target.value); setErr(''); }}>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.names[locale] ?? c.names.en} {c.dialCode}</option>
                ))}
              </select>
            </div>
            <input
              id={phoneId}
              className="field__input num"
              type="tel"
              inputMode="tel"
              autoComplete="username"
              aria-describedby={phoneHintId}
              placeholder={selectedCountry.example}
              value={phone}
              disabled={busy}
              onChange={(e) => {
                const next = parsePhoneInput(e.target.value, selectedCountry.code, countries);
                setCountry(next.country);
                setPhone(next.number);
                setErr('');
              }}
              required
            />
          </div>
          <span className="field__hint" id={phoneHintId}>
            {t('auth.phoneHint', { example: `${selectedCountry.dialCode} ${selectedCountry.example}` })}
          </span>
        </div>}

        <label className="field">
          <span className="field__label">{t('auth.password')}</span>
          <input
            className="field__input"
            type="password"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            placeholder={t('auth.passwordHint')}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            minLength={8}
            required
          />
        </label>

        {/* role=alert：错误一出现就念给屏幕阅读器听，不用等用户摸到 */}
        {err && <p className="field__err" role="alert">{err}</p>}

        <Button variant="primary" type="submit" disabled={busy || !selectedCountry} className="btn--wide">
          {busy ? t('action.working')
                : t(mode === 'login' ? 'action.login' : 'action.register')}
        </Button>

        <button type="button" className="linkish"
                disabled={busy} onClick={changeMode}>
          {t(mode === 'login' ? 'auth.toRegister' : 'auth.toLogin')}
        </button>

        {mode === 'register' && (
          <p className="small dim auth__note">{t('auth.noSmsYet')}</p>
        )}
      </form>
    </Sheet>
  );
}
